import { TBillingPeriod } from "../features/pricingPlans/pricingPlans.types";

declare global {
  interface Window {
    dataLayer: Array<Record<string, any>>;
  }
}

/** Ensures window.dataLayer exists */
function dl() {
  if (typeof window === "undefined") return [] as any[];
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/** Generic event push */
export function trackEvent(event: string, params: Record<string, any> = {}) {
  dl().push({ event, ...params });
}

/**
 * User begins checkout process
 * GA4 recommended event: "begin_checkout"
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events#begin_checkout
 */
export function trackBeginCheckout(params: {
  userId: string;
  name: string;
  email: string;
  planId: string;
  planName: string;
  planPrice: number;
  planDuration?: TBillingPeriod;
  currency?: string;
}) {
  dl().push({ ecommerce: null });
  trackEvent("begin_checkout", {
    user_id: params.userId,
    user_name: params.name,
    email: params.email,
    ecommerce: {
      currency: params.currency ?? "USD",
      value: params.planPrice,
      items: [
        {
          item_id: params.planId,
          item_name: params.planName,
          price: params.planPrice,
          quantity: 1,
          item_category: "subscription",
          plan_duration: params.planDuration,
        },
      ],
    },
  });
}

/**
 * User selects a subscription plan or tier
 * Custom event: "plan_selected"
 */
export function trackPlanSelected(params: {
  userId: string;
  name: string;
  email: string;
  planId: string;
  planName: string;
  planPrice: number;
  planDuration?: TBillingPeriod;
  currency?: string;
}) {
  dl().push({ ecommerce: null });
  trackEvent("plan_selected", {
    user_id: params.userId,
    user_name: params.name,
    email: params.email,
    ecommerce: {
      currency: params.currency ?? "USD",
      value: params.planPrice,
      items: [
        {
          item_id: params.planId,
          item_name: params.planName,
          price: params.planPrice,
          quantity: 1,
          item_category: "subscription",
          plan_duration: params.planDuration,
        },
      ],
    },
  });
}

/**
 * User completes a purchase
 * GA4 recommended event: "purchase"
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
 */
export function trackPurchase(params: {
  userId: string;
  name: string;
  email: string;
  planId: string;
  planName: string;
  planPrice: number;
  planDuration?: TBillingPeriod;
  currency?: string;
  transactionId?: string;
}) {
  trackEvent("purchase", {
    user_id: params.userId,
    user_name: params.name,
    email: params.email,
    ecommerce: {
      transaction_id: params.transactionId || "",
      currency: params.currency ?? "USD",
      value: params.planPrice,
      tax: 0,
      shipping: 0,
      items: [
        {
          item_id: params.planId,
          item_name: params.planName,
          price: params.planPrice,
          quantity: 1,
          item_category: "subscription",
          plan_duration: params.planDuration,
        },
      ],
    },
  });
}

/**
 * User completes an upsell purchase
 * Custom event: "upsell_purchase"
 */
export function trackUpsellPurchase(params: {
  userId: string;
  name: string;
  email: string;
  planId: string;
  planName: string;
  planPrice: number;
  planDuration?: TBillingPeriod;
  currency?: string;
  transactionId?: string;
}) {
  trackEvent("upsell_purchase", {
    user_id: params.userId,
    user_name: params.name,
    email: params.email,
    ecommerce: {
      transaction_id: params.transactionId || "",
      currency: params.currency ?? "USD",
      value: params.planPrice,
      tax: 0,
      shipping: 0,
      items: [
        {
          item_id: params.planId,
          item_name: params.planName,
          price: params.planPrice,
          quantity: 1,
          item_category: "subscription",
          plan_duration: params.planDuration,
        },
      ],
    },
  });
}

/**
 * Signup completed (account created successfully)
 * Custom event: "signup_complete"
 */
export function trackSignupComplete({
  method,
  userId,
  name,
  email,
  registeredFrom,
}: {
  method: "email";
  userId: string;
  name: string;
  email: string;
  registeredFrom: "extension" | "website";
}) {
  trackEvent("signup_complete", {
    method,
    user_id: userId,
    name,
    email,
    registered_from: registeredFrom,
  });
}

/**
 * User views the pricing page or pricing section
 * Custom event: "view_pricing"
 */
export function trackViewPricing(params: {
  userId: string;
  name: string;
  email: string;
  plans: Array<{
    id: string;
    name: string;
    price: number;
    period?: TBillingPeriod | null;
  }>;
  currency?: string;
}) {
  trackEvent("view_pricing", {
    user_id: params.userId,
    user_name: params.name,
    email: params.email,
    ecommerce: {
      currency: params.currency ?? "USD",
      value: 0,
      items: params.plans.map((plan) => ({
        item_id: plan.id,
        item_name: plan.name,
        price: plan.price,
        quantity: 1,
        item_category: "subscription",
        plan_duration: plan.period,
      })),
    },
  });
}

/**
 * Chrome install completed
 * Custom event: "chrome_install"
 */
export function trackChromeInstall({
  userId,
}: {
  userId?: string;
} = {}) {
  trackEvent("chrome_install", {
    user_id: userId || "",
  });
}
